import { useState } from 'react'
import { admin, type AdminInvitation } from '../../api/admin'
import { ApiError } from '../../api/client'
import { TIMEPOINT_VALUES } from '../../api/know'

/**
 * Modal para crear invitaciones a un cuestionario. Lo usa AdminQuestionnaires
 * y AdminInvitations (botón "+ Invitar"). Dos modos:
 *   - **individual** (default): un email → una invitación. Después muestra el
 *     link copiable + estado del mail.
 *   - **masivo** (toggle "Invitar a varios", PRD §8.2): un textarea con muchos
 *     emails (uno por línea o separados por coma) → POST /admin/invitations/bulk.
 *     Muestra un resumen created/reused/failed.
 *
 * En ambos modos un select opcional de `timepoint` (T1/T3/T4) permite marcar
 * la ola de re-test. "unica" = sin marcar (single-shot, comportamiento previo).
 *
 * No es un componente reutilizable de la design system — es un modal ad-hoc
 * inline; si crece, sacarlo a un Dialog genérico.
 */
export function InviteModal({
  defaultCode,
  codes,
  onClose,
  defaultEmail,
}: {
  defaultCode: string
  codes: Array<{ code: string; name_es: string }>
  onClose: () => void
  defaultEmail?: string
}) {
  const [bulk, setBulk] = useState(false)
  const [email, setEmail] = useState(defaultEmail ?? '')
  const [emailsText, setEmailsText] = useState('')
  const [code, setCode] = useState(defaultCode)
  // '' = "unica" (no marca de timepoint). Los valores no-unica se mandan al backend.
  const [timepoint, setTimepoint] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    invitation: AdminInvitation
    link_url: string
    email_sent: boolean
  } | null>(null)
  const [bulkResult, setBulkResult] = useState<{
    created: number
    reused: number
    failed: Array<{ email: string; reason: string }>
  } | null>(null)
  const [copied, setCopied] = useState(false)

  const tp = timepoint || undefined

  function parseEmails(raw: string): string[] {
    return raw
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0)
  }

  const parsedEmails = parseEmails(emailsText)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (bulk) {
        const r = await admin.bulkInvitations(parsedEmails, code, tp)
        setBulkResult(r)
      } else {
        const r = await admin.createInvitation(email.trim().toLowerCase(), code, tp)
        setResult(r)
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  function copyLink() {
    if (!result) return
    void navigator.clipboard.writeText(result.link_url).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }

  function resetForm() {
    setResult(null)
    setBulkResult(null)
    setEmail('')
    setEmailsText('')
    setCopied(false)
    setError(null)
  }

  const done = result !== null || bulkResult !== null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-md backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="panel flex max-w-md flex-col gap-md p-xl shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-md border-b border-outline pb-md">
          <h2 className="m-0 text-base font-medium normal-case tracking-normal text-text-primary">
            {bulk ? 'Invitar a varios' : 'Invitar a un cuestionario'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-outline bg-transparent text-sm text-text-muted hover:border-primary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit} className="flex flex-col gap-md">
            <label className="flex items-center gap-sm text-xs font-medium text-text-muted">
              <input
                type="checkbox"
                checked={bulk}
                onChange={(e) => {
                  setBulk(e.target.checked)
                  setError(null)
                }}
              />
              Invitar a varios (carga masiva)
            </label>

            {bulk ? (
              <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
                Emails (uno por línea o separados por coma)
                <textarea
                  className="input min-h-[120px] resize-y font-mono text-xs"
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  placeholder={'jugador1@club.com\njugador2@club.com'}
                  autoFocus
                />
                <span className="text-text-muted">
                  {parsedEmails.length} email
                  {parsedEmails.length === 1 ? '' : 's'} detectado
                  {parsedEmails.length === 1 ? '' : 's'}.
                </span>
              </label>
            ) : (
              <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
                Email del invitado
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@dominio.com"
                  required
                  autoFocus
                  maxLength={254}
                />
              </label>
            )}

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Cuestionario
              <select
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              >
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name_es}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Momento (opcional — re-test)
              <select
                className="input"
                value={timepoint}
                onChange={(e) => setTimepoint(e.target.value)}
              >
                <option value="">Sin marcar (única)</option>
                {TIMEPOINT_VALUES.filter((t) => t !== 'unica').map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>

            {error && <div className="banner-err">{error}</div>}
            <div className="flex justify-end gap-sm">
              <button type="button" className="btn" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  busy ||
                  (bulk ? parsedEmails.length === 0 : !email.includes('@'))
                }
              >
                {busy
                  ? 'Creando…'
                  : bulk
                    ? `Invitar (${parsedEmails.length})`
                    : 'Crear invitación'}
              </button>
            </div>
          </form>
        ) : bulkResult ? (
          <div className="flex flex-col gap-md">
            <div className="banner-ok">
              {bulkResult.created} creada
              {bulkResult.created === 1 ? '' : 's'} · {bulkResult.reused}{' '}
              reutilizada{bulkResult.reused === 1 ? '' : 's'} ·{' '}
              {bulkResult.failed.length} con error.
            </div>
            {bulkResult.failed.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResult.failed.map((f, i) => (
                      <tr key={`${f.email}-${i}`}>
                        <td className="font-mono text-xs">{f.email || '(vacío)'}</td>
                        <td className="text-text-muted">{f.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-sm">
              <button type="button" className="btn" onClick={resetForm}>
                Otra carga
              </button>
              <button type="button" className="btn-primary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        ) : result ? (
          <div className="flex flex-col gap-md">
            <div className="banner-ok">
              Invitación lista para <strong>{result.invitation.email}</strong>{' '}
              · {result.invitation.questionnaire_code}
              {result.invitation.timepoint
                ? ` · ${result.invitation.timepoint}`
                : ''}
              .
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-text-muted">
                Mail
              </div>
              <div className="text-sm">
                {result.email_sent ? (
                  // `email_sent` del backend = "hay transporte y la invitación
                  // no está agendada", no un acuse de envío: el mail se encola
                  // y sale por el freno global del outbox, y una invitación
                  // reusada no re-encola nada. Decirlo así evita el "✓ Mail
                  // enviado" que prometía algo que el sistema no sabe.
                  <span className="text-goal">
                    Mail encolado si la invitación es nueva (una reusada no
                    re-encola: usá "Reenviar mail" en la lista). Sale por la
                    cola de /admin/emails, que es donde se ve si salió.
                  </span>
                ) : (
                  <span className="text-text-muted">
                    SMTP no configurado — copiá el link y envialo manualmente
                    (WhatsApp / mail personal / etc).
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-text-muted">
                Link
              </div>
              <div className="mt-xs flex items-center gap-sm">
                <input
                  type="text"
                  className="input text-xs"
                  readOnly
                  value={result.link_url}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  className="btn shrink-0"
                  onClick={copyLink}
                >
                  {copied ? 'Copiado ✓' : 'Copiar'}
                </button>
              </div>
              <div className="mt-xs text-xs text-text-muted">
                Vence el{' '}
                {new Date(result.invitation.expires_at).toLocaleDateString(
                  'es-AR',
                  { dateStyle: 'medium' },
                )}
                .
              </div>
            </div>

            <div className="flex justify-end gap-sm">
              <button type="button" className="btn" onClick={resetForm}>
                Otra invitación
              </button>
              <button type="button" className="btn-primary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
